import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { useForm } from 'react-hook-form';
import { toast } from 'sonner';
import { 
  Settings, LogOut, ShoppingBag, Package, Newspaper, Camera, Star, User,
  Search, Plus, Edit, Trash, Eye, EyeOff, DollarSign, FileText, Upload, X
} from 'lucide-react';

import { Button } from '@/components/ui/button';
import { Card } from '@/components/ui/card';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';

// Import modular components
import {
  SaleToggle,
  ImageDropZone,
  PhotoModerationPanel,
  ReviewModerationPanel,
  UsersManagementPanel,
  InlinePriceEditor,
  QuickEditProduct,
  SiteStatusControl,
  PickupDelayControl
} from './admin/components';
import { API, adminApi } from './admin/utils';

// Main Admin Component
const Admin = () => {
  const [isAuthenticated, setIsAuthenticated] = useState(false);
  const [token, setToken] = useState(null);
  const [products, setProducts] = useState([]);
  const [orders, setOrders] = useState([]);
  const [blogPosts, setBlogPosts] = useState([]);
  const [activeTab, setActiveTab] = useState('products');
  const [searchQuery, setSearchQuery] = useState('');
  const [showAdminPassword, setShowAdminPassword] = useState(false);
  
  const [showProductDialog, setShowProductDialog] = useState(false);
  const [showBlogDialog, setShowBlogDialog] = useState(false);
  const [editingProduct, setEditingProduct] = useState(null);
  const [editingBlogPost, setEditingBlogPost] = useState(null);
  
  const [productImages, setProductImages] = useState([]);
  const [blogImage, setBlogImage] = useState('');
  const [newProductSalePrice, setNewProductSalePrice] = useState(null);
  
  const { register, handleSubmit, reset, setValue, watch } = useForm({
    defaultValues: {
      category: 'plants',
      featured: false
    }
  });
  const { register: registerLogin, handleSubmit: handleSubmitLogin } = useForm();
  const { register: registerBlog, handleSubmit: handleSubmitBlog, reset: resetBlog, setValue: setValueBlog } = useForm();

  const watchPrice = watch('price');

  useEffect(() => {
    const savedToken = localStorage.getItem('admin_token');
    if (savedToken) {
      setToken(savedToken);
      setIsAuthenticated(true);
      fetchData(savedToken);
    }
  }, []);

  const [isLoading, setIsLoading] = useState(false);
  
  // Optimized: Only fetch what's needed
  const fetchData = async (authToken, forceRefresh = false) => {
    if (isLoading && !forceRefresh) return;
    setIsLoading(true);
    try {
      const [productsRes, ordersRes, blogRes] = await Promise.all([
        axios.get(`${API}/products`),
        axios.get(`${API}/admin/orders`, { headers: { Authorization: `Bearer ${authToken}` } }),
        axios.get(`${API}/blog?published_only=false`)
      ]);
      setProducts(productsRes.data || []);
      setOrders(ordersRes.data || []);
      setBlogPosts(blogRes.data || []);
    } catch (error) {
      console.error('Error fetching data:', error);
      toast.error('Failed to load data');
    } finally {
      setIsLoading(false);
    }
  };

  // Optimistic update helpers
  const updateProductLocally = (productId, updates) => {
    setProducts(prev => prev.map(p => 
      p._id === productId ? { ...p, ...updates } : p
    ));
  };

  const removeProductLocally = (productId) => {
    setProducts(prev => prev.filter(p => p._id !== productId));
  };

  const removeOrderLocally = (orderId) => {
    setOrders(prev => prev.filter(o => o._id !== orderId));
  };

  const handleLogin = async (data) => {
    try {
      const response = await axios.post(`${API}/admin/login`, data);
      const accessToken = response.data.access_token;
      setToken(accessToken);
      localStorage.setItem('admin_token', accessToken);
      setIsAuthenticated(true);
      fetchData(accessToken);
      toast.success('Welcome back!');
    } catch (error) {
      toast.error('Invalid credentials');
    }
  };

  const handleLogout = () => {
    setToken(null);
    localStorage.removeItem('admin_token');
    setIsAuthenticated(false);
  };

  // Product handlers
  const handleCreateProduct = async (data) => {
    try {
      const [mainImage, ...galleryImages] = productImages;
      const productData = {
        ...data,
        price: parseFloat(data.price),
        sale_price: newProductSalePrice,
        inventory_quantity: parseInt(data.inventory_quantity),
        display_order: data.display_order ? parseInt(data.display_order) : null,
        image_url: mainImage || '',
        image_gallery: galleryImages.map(url => ({ url, alt_text: '' }))
      };

      await axios.post(`${API}/admin/products`, productData, {
        headers: { Authorization: `Bearer ${token}` }
      });
      
      toast.success('Product created!');
      setShowProductDialog(false);
      setProductImages([]);
      setNewProductSalePrice(null);
      reset();
      fetchData(token, true);
    } catch (error) {
      console.error('Create product error:', error.response?.data || error);
      toast.error(error.response?.data?.detail || 'Failed to create product');
    }
  };

  const handleUpdateProduct = async (productId, updates) => {
    updateProductLocally(productId, updates);
    setEditingProduct(null);
    try {
      await axios.put(`${API}/admin/products/${productId}`, updates, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success('Product updated!');
    } catch (error) {
      toast.error('Failed to update product');
      fetchData(token, true);
    }
  };

  const handleInlineUpdate = async (productId, updates) => {
    updateProductLocally(productId, updates);
    try {
      await axios.put(`${API}/admin/products/${productId}`, updates, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success('Price updated!');
    } catch (error) {
      toast.error('Failed to update');
      fetchData(token, true);
    }
  };

  const handleQuickInventory = async (productId, change) => {
    const product = products.find(p => p._id === productId);
    if (product) {
      updateProductLocally(productId, { 
        inventory_quantity: product.inventory_quantity + change 
      });
    }
    try {
      await axios.put(
        `${API}/admin/products/${productId}/inventory?quantity=${change}`,
        {},
        { headers: { Authorization: `Bearer ${token}` } }
      );
      toast.success(`Stock ${change > 0 ? 'added' : 'removed'}!`);
    } catch (error) {
      toast.error('Failed to update inventory');
      fetchData(token, true);
    }
  };

  const handleDeleteProduct = async (productId) => {
    if (!window.confirm('Delete this product? This cannot be undone.')) return;
    removeProductLocally(productId);
    try {
      await axios.delete(`${API}/admin/products/${productId}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success('Product deleted');
    } catch (error) {
      toast.error('Failed to delete product');
      fetchData(token, true);
    }
  };

  const handleDeleteOrder = async (orderId, orderNumber) => {
    if (!window.confirm(`Delete order ${orderNumber}? This cannot be undone.`)) return;
    removeOrderLocally(orderId);
    try {
      await axios.delete(`${API}/admin/orders/${orderId}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success('Order deleted');
    } catch (error) {
      toast.error('Failed to delete order');
      fetchData(token, true);
    }
  };

  // Blog handlers
  const handleSaveBlogPost = async (data) => {
    try {
      const slug = data.title.toLowerCase().replace(/[^a-z0-9]+/g, '-').replace(/(^-|-$)/g, '');
      const postData = { ...data, slug, featured_image: blogImage, published: true };

      if (editingBlogPost) {
        await axios.put(`${API}/admin/blog/${editingBlogPost._id}`, postData, {
          headers: { Authorization: `Bearer ${token}` }
        });
        toast.success('Blog post updated!');
      } else {
        await axios.post(`${API}/admin/blog`, postData, {
          headers: { Authorization: `Bearer ${token}` }
        });
        toast.success('Blog post published!');
      }

      setShowBlogDialog(false);
      setEditingBlogPost(null);
      setBlogImage('');
      resetBlog();
      fetchData(token);
    } catch (error) {
      toast.error('Failed to save blog post');
    }
  };

  const handleEditBlogPost = (post) => {
    setEditingBlogPost(post);
    setValueBlog('title', post.title);
    setValueBlog('excerpt', post.excerpt);
    setValueBlog('content', post.content);
    setValueBlog('author', post.author || 'AquaPlants Plus');
    setBlogImage(post.featured_image || '');
    setShowBlogDialog(true);
  };

  const handleDeleteBlogPost = async (postId) => {
    if (!window.confirm('Delete this blog post?')) return;
    try {
      await axios.delete(`${API}/admin/blog/${postId}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success('Blog post deleted');
      fetchData(token);
    } catch (error) {
      toast.error('Failed to delete blog post');
    }
  };

  const filteredProducts = products.filter(p => 
    p.name.toLowerCase().includes(searchQuery.toLowerCase()) ||
    p.category.toLowerCase().includes(searchQuery.toLowerCase())
  );

  // Login Screen
  if (!isAuthenticated) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-secondary/20" data-testid="admin-login-page">
        <Card className="w-full max-w-md p-8">
          <div className="text-center mb-6">
            <div className="w-16 h-16 bg-primary/10 rounded-full flex items-center justify-center mx-auto mb-4">
              <Settings className="w-8 h-8 text-primary" />
            </div>
            <h2 className="heading-font text-2xl font-bold">Admin Dashboard</h2>
            <p className="text-sm text-muted-foreground">Sign in to manage your store</p>
          </div>
          <form onSubmit={handleSubmitLogin(handleLogin)} className="space-y-4">
            <div>
              <Label htmlFor="email">Email</Label>
              <Input id="email" type="email" {...registerLogin('email', { required: true })} className="mt-1" data-testid="admin-email-input" />
            </div>
            <div>
              <Label htmlFor="password">Password</Label>
              <div className="relative mt-1">
                <Input 
                  id="password" 
                  type={showAdminPassword ? 'text' : 'password'} 
                  {...registerLogin('password', { required: true })} 
                  className="pr-10" 
                  data-testid="admin-password-input" 
                />
                <button
                  type="button"
                  onClick={() => setShowAdminPassword(!showAdminPassword)}
                  className="absolute right-3 top-1/2 -translate-y-1/2 text-muted-foreground hover:text-foreground transition-colors"
                  data-testid="admin-toggle-password"
                >
                  {showAdminPassword ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                </button>
              </div>
            </div>
            <Button type="submit" className="w-full" data-testid="admin-login-btn">Sign In</Button>
          </form>
        </Card>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-secondary/20" data-testid="admin-dashboard">
      {/* Header */}
      <div className="bg-white border-b sticky top-0 z-40">
        <div className="container mx-auto px-4 md:px-8 max-w-7xl">
          <div className="flex items-center justify-between h-16">
            <h1 className="heading-font text-xl font-bold">Admin Dashboard</h1>
            <Button onClick={handleLogout} variant="ghost" size="sm">
              <LogOut className="w-4 h-4 mr-2" /> Logout
            </Button>
          </div>
        </div>
      </div>

      <div className="container mx-auto px-4 md:px-8 max-w-7xl py-6">
        {/* Site Status Control */}
        <SiteStatusControl token={token} />
        
        {/* Pickup Delay Control */}
        <PickupDelayControl token={token} />

        {/* Stats Cards */}
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-6">
          <Card className="p-4">
            <p className="text-xs text-muted-foreground mb-1">Products</p>
            <p className="text-2xl font-bold">{products.length}</p>
          </Card>
          <Card className="p-4">
            <p className="text-xs text-muted-foreground mb-1">On Sale</p>
            <p className="text-2xl font-bold text-red-600">{products.filter(p => p.sale_price).length}</p>
          </Card>
          <Card className="p-4">
            <p className="text-xs text-muted-foreground mb-1">Orders</p>
            <p className="text-2xl font-bold">{orders.length}</p>
          </Card>
          <Card className="p-4 bg-amber-50 border-amber-200">
            <p className="text-xs text-amber-600 mb-1">Low Stock</p>
            <p className="text-2xl font-bold text-amber-700">
              {products.filter(p => p.inventory_quantity < 10).length}
            </p>
          </Card>
        </div>

        {/* Main Tabs */}
        <Tabs value={activeTab} onValueChange={setActiveTab} className="space-y-6">
          <TabsList className="bg-white border p-1 h-auto flex-wrap">
            <TabsTrigger value="products" className="gap-2 data-[state=active]:bg-primary data-[state=active]:text-white">
              <ShoppingBag className="w-4 h-4" /> Products
            </TabsTrigger>
            <TabsTrigger value="orders" className="gap-2 data-[state=active]:bg-primary data-[state=active]:text-white">
              <Package className="w-4 h-4" /> Orders
            </TabsTrigger>
            <TabsTrigger value="blog" className="gap-2 data-[state=active]:bg-primary data-[state=active]:text-white">
              <Newspaper className="w-4 h-4" /> Blog
            </TabsTrigger>
            <TabsTrigger value="photos" className="gap-2 data-[state=active]:bg-primary data-[state=active]:text-white">
              <Camera className="w-4 h-4" /> Photos
            </TabsTrigger>
            <TabsTrigger value="reviews" className="gap-2 data-[state=active]:bg-primary data-[state=active]:text-white">
              <Star className="w-4 h-4" /> Reviews
            </TabsTrigger>
            <TabsTrigger value="users" className="gap-2 data-[state=active]:bg-primary data-[state=active]:text-white">
              <User className="w-4 h-4" /> Users
            </TabsTrigger>
          </TabsList>

          {/* Products Tab */}
          <TabsContent value="products" className="space-y-4">
            <div className="flex flex-col sm:flex-row gap-4 justify-between">
              <div className="relative flex-1 max-w-md">
                <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
                <Input 
                  placeholder="Search products..." 
                  value={searchQuery}
                  onChange={e => setSearchQuery(e.target.value)}
                  className="pl-10"
                />
              </div>
              <Button onClick={() => { setProductImages([]); setNewProductSalePrice(null); reset(); setShowProductDialog(true); }} className="gap-2">
                <Plus className="w-4 h-4" /> Add Product
              </Button>
            </div>

            <div className="grid gap-3">
              {filteredProducts.map((product) => (
                <Card key={product._id} className="p-4" data-testid={`admin-product-${product._id}`}>
                  <div className="flex gap-4 items-start">
                    <img src={product.image_url} alt={product.name} className="w-16 h-16 rounded-lg object-cover flex-shrink-0" loading="lazy" />
                    <div className="flex-1 min-w-0">
                      <div className="flex items-start justify-between gap-2">
                        <div>
                          <h3 className="font-semibold truncate">{product.name}</h3>
                          <p className="text-xs text-muted-foreground capitalize">{product.category}</p>
                        </div>
                        <div className="flex items-center gap-1 flex-shrink-0">
                          <span className={`text-xs font-medium px-2 py-1 rounded ${product.inventory_quantity < 10 ? 'bg-amber-100 text-amber-700' : 'bg-secondary'}`}>
                            {product.inventory_quantity} stock
                          </span>
                          <Button variant="outline" size="sm" onClick={() => handleQuickInventory(product._id, -1)}>-1</Button>
                          <Button variant="outline" size="sm" onClick={() => handleQuickInventory(product._id, 5)}>+5</Button>
                          <Button variant="ghost" size="icon" onClick={() => setEditingProduct(product)}>
                            <Edit className="w-4 h-4" />
                          </Button>
                          <Button variant="ghost" size="icon" className="text-destructive" onClick={() => handleDeleteProduct(product._id)}>
                            <Trash className="w-4 h-4" />
                          </Button>
                        </div>
                      </div>
                      <div className="mt-2">
                        <InlinePriceEditor 
                          product={product}
                          onUpdate={(updates) => handleInlineUpdate(product._id, updates)}
                        />
                      </div>
                    </div>
                  </div>
                </Card>
              ))}
            </div>
          </TabsContent>

          {/* Orders Tab */}
          <TabsContent value="orders" className="space-y-4">
            {orders.length === 0 ? (
              <Card className="p-12 text-center">
                <Package className="w-12 h-12 text-muted-foreground mx-auto mb-4" />
                <p className="text-muted-foreground">No orders yet</p>
              </Card>
            ) : (
              <div className="grid gap-3">
                {orders.map((order) => (
                  <Card key={order._id} className="p-4">
                    <div className="flex justify-between items-start">
                      <div>
                        <h3 className="font-semibold">{order.order_number}</h3>
                        <p className="text-sm text-muted-foreground">{order.customer_name}</p>
                        <p className="text-xs text-muted-foreground">{order.customer_email}</p>
                      </div>
                      <div className="text-right flex flex-col items-end gap-2">
                        <p className="font-bold text-lg">${order.total?.toFixed(2) || '0.00'}</p>
                        <span className={`text-xs px-2 py-1 rounded-full ${
                          order.status === 'completed' ? 'bg-green-100 text-green-700' :
                          order.status === 'pending' ? 'bg-amber-100 text-amber-700' : 'bg-secondary'
                        }`}>
                          {order.status}
                        </span>
                        <Button 
                          variant="ghost" 
                          size="sm" 
                          className="text-red-500 hover:text-red-700 hover:bg-red-50 h-7 px-2"
                          onClick={() => handleDeleteOrder(order._id, order.order_number)}
                          data-testid={`delete-order-${order._id}`}
                        >
                          <Trash className="w-3 h-3 mr-1" /> Delete
                        </Button>
                      </div>
                    </div>
                  </Card>
                ))}
              </div>
            )}
          </TabsContent>

          {/* Blog Tab */}
          <TabsContent value="blog" className="space-y-4">
            <div className="flex justify-end">
              <Button onClick={() => { setBlogImage(''); resetBlog(); setEditingBlogPost(null); setShowBlogDialog(true); }} className="gap-2">
                <Plus className="w-4 h-4" /> New Blog Post
              </Button>
            </div>

            {blogPosts.length === 0 ? (
              <Card className="p-12 text-center">
                <Newspaper className="w-12 h-12 text-muted-foreground mx-auto mb-4" />
                <p className="text-muted-foreground">No blog posts yet</p>
              </Card>
            ) : (
              <div className="grid gap-3">
                {blogPosts.map((post) => (
                  <Card key={post._id} className="p-4">
                    <div className="flex gap-4 items-center">
                      {post.featured_image ? (
                        <img src={post.featured_image} alt={post.title} className="w-20 h-20 rounded-lg object-cover flex-shrink-0" />
                      ) : (
                        <div className="w-20 h-20 rounded-lg bg-primary/10 flex items-center justify-center flex-shrink-0">
                          <FileText className="w-8 h-8 text-primary/50" />
                        </div>
                      )}
                      <div className="flex-1 min-w-0">
                        <h3 className="font-semibold">{post.title}</h3>
                        <p className="text-sm text-muted-foreground line-clamp-1">{post.excerpt}</p>
                        <p className="text-xs text-muted-foreground mt-1">{post.author} • {new Date(post.created_at).toLocaleDateString()}</p>
                      </div>
                      <div className="flex items-center gap-1">
                        <Button variant="ghost" size="icon" onClick={() => window.open(`/blog/${post.slug}`, '_blank')}>
                          <Eye className="w-4 h-4" />
                        </Button>
                        <Button variant="ghost" size="icon" onClick={() => handleEditBlogPost(post)}>
                          <Edit className="w-4 h-4" />
                        </Button>
                        <Button variant="ghost" size="icon" className="text-destructive" onClick={() => handleDeleteBlogPost(post._id)}>
                          <Trash className="w-4 h-4" />
                        </Button>
                      </div>
                    </div>
                  </Card>
                ))}
              </div>
            )}
          </TabsContent>

          {/* Photo Reviews Tab */}
          <TabsContent value="photos" className="space-y-4">
            <PhotoModerationPanel token={token} />
          </TabsContent>

          {/* Reviews Moderation Tab */}
          <TabsContent value="reviews" className="space-y-4">
            <ReviewModerationPanel token={token} />
          </TabsContent>

          {/* Users Management Tab */}
          <TabsContent value="users" className="space-y-4">
            <UsersManagementPanel token={token} />
          </TabsContent>
        </Tabs>
      </div>

      {/* Add Product Dialog */}
      <Dialog open={showProductDialog} onOpenChange={setShowProductDialog}>
        <DialogContent className="w-[95vw] max-w-2xl max-h-[85vh] overflow-y-auto p-4 sm:p-6">
          <DialogHeader>
            <DialogTitle>Add New Product</DialogTitle>
          </DialogHeader>
          <form onSubmit={handleSubmit(handleCreateProduct)} className="space-y-4">
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              <div className="sm:col-span-2">
                <Label>Product Name *</Label>
                <Input {...register('name', { required: true })} placeholder="e.g., Java Fern" />
              </div>
              <div className="sm:col-span-2">
                <Label>Description *</Label>
                <Textarea {...register('description', { required: true })} placeholder="Describe the product..." rows={2} />
              </div>
              <div>
                <Label>Category *</Label>
                <Select onValueChange={v => setValue('category', v)} defaultValue="plants">
                  <SelectTrigger><SelectValue placeholder="Select category" /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="plants">Plants</SelectItem>
                    <SelectItem value="bundles">Bundles</SelectItem>
                    <SelectItem value="invertebrates">Invertebrates</SelectItem>
                    <SelectItem value="fish">Fish</SelectItem>
                    <SelectItem value="hardscape">Hardscape</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <div>
                <Label>SKU *</Label>
                <Input {...register('sku', { required: true })} placeholder="PLANT-001" />
              </div>
            </div>

            {/* Pricing Section */}
            <div className="bg-secondary/30 rounded-lg p-4 space-y-3">
              <div className="flex items-center gap-2">
                <DollarSign className="w-4 h-4 text-primary" />
                <Label className="text-base font-semibold">Pricing & Ordering</Label>
              </div>
              <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
                <div>
                  <Label>Regular Price ($) *</Label>
                  <Input type="number" step="0.01" {...register('price', { required: true })} placeholder="9.99" />
                </div>
                <div>
                  <Label>Stock Quantity *</Label>
                  <Input type="number" {...register('inventory_quantity', { required: true })} placeholder="10" />
                </div>
                <div>
                  <Label>Display Order</Label>
                  <Input type="number" {...register('display_order')} placeholder="Auto" />
                  <p className="text-xs text-muted-foreground mt-1">Lower = first</p>
                </div>
              </div>
              
              {watchPrice && (
                <SaleToggle 
                  price={parseFloat(watchPrice) || 0}
                  salePrice={newProductSalePrice}
                  onSaleChange={setNewProductSalePrice}
                />
              )}
            </div>

            <div className="flex items-center gap-2">
              <input type="checkbox" id="featured" {...register('featured')} />
              <Label htmlFor="featured" className="cursor-pointer">Featured product</Label>
            </div>

            <ImageDropZone
              images={productImages}
              onImageAdd={setProductImages}
              onImageRemove={setProductImages}
              label="Product Images (drag & drop URLs or paste)"
            />

            <Button type="submit" className="w-full">Create Product</Button>
          </form>
        </DialogContent>
      </Dialog>

      {/* Edit Product Dialog */}
      <Dialog open={!!editingProduct} onOpenChange={() => setEditingProduct(null)}>
        <DialogContent className="w-[95vw] max-w-2xl max-h-[85vh] overflow-y-auto p-4 sm:p-6">
          <DialogHeader>
            <DialogTitle>Edit Product</DialogTitle>
          </DialogHeader>
          {editingProduct && (
            <QuickEditProduct
              product={editingProduct}
              onSave={(updates) => handleUpdateProduct(editingProduct._id, updates)}
              onClose={() => setEditingProduct(null)}
            />
          )}
        </DialogContent>
      </Dialog>

      {/* Blog Dialog */}
      <Dialog open={showBlogDialog} onOpenChange={(open) => {
        setShowBlogDialog(open);
        if (!open) { setEditingBlogPost(null); setBlogImage(''); resetBlog(); }
      }}>
        <DialogContent className="max-w-3xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>{editingBlogPost ? 'Edit Blog Post' : 'Create New Blog Post'}</DialogTitle>
          </DialogHeader>
          <form onSubmit={handleSubmitBlog(handleSaveBlogPost)} className="space-y-4">
            <div>
              <Label>Title *</Label>
              <Input {...registerBlog('title', { required: true })} placeholder="Enter blog post title..." />
            </div>
            <div>
              <Label>Excerpt (shown on blog cards) *</Label>
              <Textarea {...registerBlog('excerpt', { required: true })} placeholder="A brief summary..." rows={2} />
            </div>
            <div>
              <Label>Featured Image</Label>
              <div 
                className={`mt-1 border-2 border-dashed rounded-lg p-4 text-center cursor-pointer transition-colors ${
                  blogImage ? 'border-green-300 bg-green-50' : 'border-gray-300 hover:border-primary hover:bg-primary/5'
                }`}
                onDragOver={(e) => { e.preventDefault(); e.currentTarget.classList.add('border-primary', 'bg-primary/10'); }}
                onDragLeave={(e) => { e.currentTarget.classList.remove('border-primary', 'bg-primary/10'); }}
                onDrop={async (e) => {
                  e.preventDefault();
                  e.currentTarget.classList.remove('border-primary', 'bg-primary/10');
                  const files = e.dataTransfer.files;
                  if (files && files.length > 0) {
                    const file = files[0];
                    if (!['image/jpeg', 'image/png', 'image/webp', 'image/gif'].includes(file.type)) {
                      toast.error('Invalid file type');
                      return;
                    }
                    try {
                      const formData = new FormData();
                      formData.append('file', file);
                      const response = await adminApi.upload('/admin/upload-blog-image', formData);
                      if (response.data.success) {
                        setBlogImage(response.data.url);
                        toast.success('Image uploaded!');
                      }
                    } catch (err) {
                      toast.error('Upload failed');
                    }
                  }
                }}
                onClick={() => document.getElementById('blog-image-input').click()}
              >
                <input
                  id="blog-image-input"
                  type="file"
                  accept="image/jpeg,image/png,image/webp,image/gif"
                  className="hidden"
                  onChange={async (e) => {
                    const file = e.target.files?.[0];
                    if (file) {
                      try {
                        const formData = new FormData();
                        formData.append('file', file);
                        const response = await adminApi.upload('/admin/upload-blog-image', formData);
                        if (response.data.success) {
                          setBlogImage(response.data.url);
                          toast.success('Image uploaded!');
                        }
                      } catch (err) {
                        toast.error('Upload failed');
                      }
                    }
                  }}
                />
                {blogImage ? (
                  <div className="relative">
                    <img src={blogImage.startsWith('/api') ? `${API.replace('/api', '')}${blogImage}` : blogImage} alt="Preview" className="w-full h-48 object-cover rounded-lg" />
                    <Button type="button" variant="destructive" size="icon" className="absolute top-2 right-2" onClick={(e) => { e.stopPropagation(); setBlogImage(''); }}>
                      <X className="w-4 h-4" />
                    </Button>
                  </div>
                ) : (
                  <div className="py-6">
                    <Upload className="w-8 h-8 mx-auto text-gray-400 mb-2" />
                    <p className="text-sm text-gray-500">Drop image here or click to browse</p>
                    <p className="text-xs text-gray-400 mt-1">JPEG, PNG, WebP, GIF • Max 25MB</p>
                  </div>
                )}
              </div>
            </div>
            <div>
              <Label>Content * (HTML supported)</Label>
              <Textarea {...registerBlog('content', { required: true })} placeholder="<h2>Introduction</h2><p>Write content...</p>" rows={12} className="font-mono text-sm" />
            </div>
            <div>
              <Label>Author</Label>
              <Input {...registerBlog('author')} defaultValue="AquaPlants Plus" />
            </div>
            <Button type="submit" className="w-full">{editingBlogPost ? 'Update Post' : 'Publish Post'}</Button>
          </form>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default Admin;
